import * as HelmetAsync from 'react-helmet-async';
const Helmet = HelmetAsync.Helmet;
import { Game, GamePlatform, Platform, Price } from '../types'
import { useBranding } from '@/hooks/useBranding'

interface SEOHeadProps {
    title: string
    description: string
    canonicalUrl: string
    image?: string
    type?: 'website' | 'product' | 'article'
    structuredData?: object
    noindex?: boolean
    game?: Game
    gamePlatform?: GamePlatform
    platform?: Platform
    prices?: Price[]
}

export const SEOHead: React.FC<SEOHeadProps> = ({
    title,
    description,
    canonicalUrl,
    image = 'https://spellen.land/spellen-land-512-yellow.png',
    type = 'website',
    structuredData,
    noindex = false,
    game,
    gamePlatform,
    platform,
    prices = []
}) => {
    const { siteName, getFullTitleWithSite } = useBranding()
    const fullTitle = getFullTitleWithSite(title)

    // Get the lowest price if available
    const lowestPrice = prices.length > 0
        ? prices.reduce((lowest, price) =>
            parseFloat(price.amount.toString()) < parseFloat(lowest.amount.toString()) ? price : lowest
        )
        : null

    // Generate structured data based on page type
    const generateStructuredData = () => {
        if (structuredData) {
            return structuredData
        }

        if (game && gamePlatform) {
            // Product structured data for game pages
            const product = {
                '@context': 'https://schema.org',
                '@type': 'Product',
                name: game.title,
                description: `Koop ${game.title} op ${siteName}. ${game.description || `Compare prices for ${game.title} across different stores and platforms`}`,
                image: image,
                url: canonicalUrl,
                brand: {
                    '@type': 'Brand',
                    name: game.publisher || game.developer || 'Unknown'
                },
                offers: prices.map(price => ({
                    '@type': 'Offer',
                    price: price.amount,
                    priceCurrency: price.currency,
                    availability: 'https://schema.org/InStock',
                    seller: {
                        '@type': 'Organization',
                        name: price.store.name
                    },
                    url: price.storeUrl
                })),
                aggregateRating: gamePlatform.metacriticScore ? {
                    '@type': 'AggregateRating',
                    ratingValue: gamePlatform.metacriticScore,
                    bestRating: '100',
                    worstRating: '0',
                    ...(gamePlatform.reviewCount && { reviewCount: gamePlatform.reviewCount })
                } : undefined,
                category: game.genres.map(g => g.genre.name).join(', '),
                releaseDate: game.releaseDate,
                publisher: game.publisher,
                developer: game.developer
            }
            return product
        }

        if (platform) {
            // Organization structured data for platform pages
            return {
                '@context': 'https://schema.org',
                '@type': 'CollectionPage',
                name: `${platform.name} Games`,
                description: `Browse and compare prices for ${platform.name} games`,
                url: canonicalUrl,
                mainEntity: {
                    '@type': 'ItemList',
                    name: `${platform.name} Games`,
                    description: `Collection of ${platform.name} games with price comparison`
                }
            }
        }

        // Default website structured data
        return {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: siteName,
            description: 'Compare video game prices across platforms and stores',
            url: canonicalUrl,
            potentialAction: {
                '@type': 'SearchAction',
                target: `${canonicalUrl}/search?q={search_term_string}`,
                'query-input': 'required name=search_term_string'
            }
        }
    }

    const structuredDataScript = generateStructuredData()

    return (
        <Helmet>
            {/* Basic meta tags */}
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonicalUrl} />

            {/* Robots meta tag */}
            {noindex && <meta name="robots" content="noindex, nofollow" />}

            {/* Open Graph tags */}
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:type" content={type} />
            <meta property="og:image" content={image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:site_name" content={siteName} />

            {/* Twitter Card tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {/* Game-specific meta tags */}
            {game && (
                <>
                    <meta name="game:title" content={game.title} />
                    {game.developer && <meta name="game:developer" content={game.developer} />}
                    {game.publisher && <meta name="game:publisher" content={game.publisher} />}
                    {game.releaseDate && <meta name="game:release_date" content={game.releaseDate} />}
                    {game.genres.length > 0 && (
                        <meta name="game:genre" content={game.genres.map(g => g.genre.name).join(', ')} />
                    )}
                </>
            )}

            {/* Platform-specific meta tags */}
            {platform && (
                <>
                    <meta name="platform:name" content={platform.name} />
                    <meta name="platform:slug" content={platform.slug} />
                </>
            )}

            {/* Price-specific meta tags */}
            {lowestPrice && (
                <>
                    <meta name="product:price:amount" content={lowestPrice.amount.toString()} />
                    <meta name="product:price:currency" content={lowestPrice.currency} />
                </>
            )}

            {/* Structured data */}
            <script type="application/ld+json">
                {JSON.stringify(structuredDataScript)}
            </script>
        </Helmet>
    )
} 